import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { useThrottleFn, useIntervalFn, useDocumentVisibility } from '@vueuse/core';
import { useRoute, useRouter } from 'vue-router';
import { useLoader } from '@flash-global66/g-loader';
import { authService } from '@/shared/common/services/auth.service';
import {
  updateTokens,
  getRefreshToken,
  getSharedLastActivityTime,
  getLastRefreshAttempt,
  setLastRefreshAttempt,
  clearLastRefreshAttempt,
  getRemainingTime,
  shouldRefreshToken,
} from '@/shared/common';
import { session, logOut } from '@/helpers/auth';
import { useSessionBroadcast } from './useSessionBroadcast';
import {
  LAST_ACTIVITY_TIMESTAMP_KEY,
  TOKEN_EXPIRATION_ALERT_SECONDS,
  INACTIVITY_ALERT_SECONDS,
  INACTIVITY_LOGOUT_SECONDS,
  CHECK_INTERVAL_MS,
  CHECK_INTERVAL_BACKGROUND_MS,
  ACTIVITY_THROTTLE_MS,
} from '@/shared/common/constants/auth.constant';

/**
 * Composable para manejar el refresh automático de tokens de autenticación basado en actividad del usuario.
 * 
 * Este composable detecta la actividad del usuario (movimientos de mouse, teclado, clicks) y automáticamente
 * refresca el token cuando está próximo a expirar. También gestiona la visualización de alertas de expiración
 * cuando el usuario está inactivo y el token está próximo a vencer, y coordina la sincronización del estado
 * de sesión entre múltiples pestañas del navegador.
 * 
 * @returns Objeto con el estado y funciones del composable:
 * - `showExpirationAlert`: Ref reactivo que indica si la alerta de expiración debe mostrarse
 * - `timeRemaining`: Ref reactivo con los segundos restantes antes del cierre de sesión por inactividad
 * - `extendSession`: Función para extender la sesión manualmente (refresca el token y oculta la alerta)
 * - `closeSession`: Función para cerrar la sesión manualmente
 */
export function useTokenRefresh() {
  const showExpirationAlert = ref(false);
  const timeRemaining = ref(0);
  const isSessionClosed = ref(false);
  const route = useRoute();
  const router = useRouter();
  const {
    notifyTokenRefreshed,
    notifyUserActivity,
    notifyExpirationAlertShown,
    notifyExpirationAlertHidden,
    setOnUserActivityCallback,
    setOnExpirationAlertShownCallback,
    setOnExpirationAlertHiddenCallback,
    setOnLogoutCallback,
  } = useSessionBroadcast();
  const { showLoader } = useLoader();
  const userActivityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

  let lastActivityTime = 0;
  let isRefreshing = false;
  let alertShownAt: number | null = null;

  function isPublicRoute(): boolean {
    const isPublic = route.matched.some((record) => record.meta?.requiresAuth === false);
    return isPublic;
  }

  const routeAuthCache = new Map<string, boolean>();

  function isPublicRouteByName(routeName: string | symbol | null | undefined): boolean {
    if (!routeName) {
      return false;
    }

    const routeNameStr = String(routeName);
    
    if (routeAuthCache.has(routeNameStr)) {
      return routeAuthCache.get(routeNameStr) ?? false;
    }

    try {
      const resolvedRoute = router.resolve({ name: routeNameStr });
      const isPublic = resolvedRoute.matched.some((record) => record.meta?.requiresAuth === false);
      
      routeAuthCache.set(routeNameStr, isPublic);
      
      return isPublic;
    } catch {
      routeAuthCache.set(routeNameStr, false);
      return false;
    }
  }

  function getInactivitySeconds(): number {
    const effectiveActivityTime = getSharedLastActivityTime(lastActivityTime);
    const inactivityMs = Date.now() - effectiveActivityTime;
    return Math.floor(inactivityMs / 1000);
  }

  function shouldShowExpirationAlert(): boolean {
    const inactivitySeconds = getInactivitySeconds();
    const remaining = getRemainingTime();

    return (
      inactivitySeconds >= INACTIVITY_ALERT_SECONDS &&
      remaining > 0 &&
      remaining <= TOKEN_EXPIRATION_ALERT_SECONDS
    );
  }

  async function refreshToken(): Promise<void> {
    if (isRefreshing) {
      return;
    }

    const lastAttempt = getLastRefreshAttempt();
    if (lastAttempt) {
      return;
    }

    isRefreshing = true;
    setLastRefreshAttempt();

    const result = await authService.refreshToken();

    await result.foldAsync(
      () => {
        isSessionClosed.value = true;
        pause();
        showExpirationAlert.value = false;
        clearLastRefreshAttempt();
        return logOut();
      },
      async (success) => {
        const currentRefreshToken = getRefreshToken() || '';

        updateTokens({
          idToken: success.data.idToken,
          refreshToken: success.data.refreshToken || currentRefreshToken,
          expiresIn: success.data.expiresIn,
        });

        const refreshTokenToNotify = success.data.refreshToken || currentRefreshToken;
        notifyTokenRefreshed({
          idToken: success.data.idToken,
          expiresIn: success.data.expiresIn,
          refreshToken: refreshTokenToNotify,
        });

        showExpirationAlert.value = false;
        notifyExpirationAlertHidden();
      }
    );
    isRefreshing = false;
    clearLastRefreshAttempt();
  }

  async function extendSession(): Promise<void> {
    const currentTime = Date.now();
    lastActivityTime = currentTime;
    alertShownAt = null;
    
    localStorage.setItem(LAST_ACTIVITY_TIMESTAMP_KEY, currentTime.toString());
    notifyUserActivity(currentTime);
    notifyExpirationAlertHidden();
    await refreshToken();
  }

  const closeSession = async (): Promise<void> => {
    isSessionClosed.value = true;
    pause();
    showExpirationAlert.value = false;
    alertShownAt = null;
    notifyExpirationAlertHidden();
    await logOut();
  };

  function checkAndRefreshToken(): void {
    if (shouldRefreshToken() && !isRefreshing) {
      refreshToken();
    }
  }

  async function checkTokenExpiration(): Promise<void> {
    if (isSessionClosed.value) {
      return;
    }

    const token = session();
    if (!token) {
      return;
    }

    const isPublic = isPublicRoute();
    if (isPublic) {
      return;
    }
    if (showLoader.value) {
      const currentTime = Date.now();
      lastActivityTime = currentTime;
      localStorage.setItem(LAST_ACTIVITY_TIMESTAMP_KEY, currentTime.toString());
      checkAndRefreshToken();
      return;
    }

    const remaining = getRemainingTime();

    if (remaining <= 0) {
      isSessionClosed.value = true;
      pause();
      showExpirationAlert.value = false;
      alertShownAt = null;
      await logOut();
      return;
    }

    if (showExpirationAlert.value && alertShownAt) {
      const secondsSinceAlertShown = Math.floor((Date.now() - alertShownAt) / 1000);
      const timeRemainingInAlert = INACTIVITY_LOGOUT_SECONDS - secondsSinceAlertShown;

      if (timeRemainingInAlert <= 0) {
        isSessionClosed.value = true;
        pause();
        showExpirationAlert.value = false;
        alertShownAt = null;
        await logOut();
        return;
      }

      timeRemaining.value = timeRemainingInAlert;
      return;
    }

    if (shouldShowExpirationAlert()) {
      alertShownAt = Date.now();
      timeRemaining.value = INACTIVITY_LOGOUT_SECONDS;
      showExpirationAlert.value = true;
      notifyExpirationAlertShown(INACTIVITY_LOGOUT_SECONDS);
    }
  }

  const handleUserActivity = useThrottleFn(() => {
    const currentTime = Date.now();
    lastActivityTime = currentTime;
    
    localStorage.setItem(LAST_ACTIVITY_TIMESTAMP_KEY, currentTime.toString());
    notifyUserActivity(currentTime);

    if (showExpirationAlert.value) {
      return;
    }

    checkAndRefreshToken();

    checkTokenExpiration();
  }, ACTIVITY_THROTTLE_MS);

  const isVisible = useDocumentVisibility();

  const getCheckInterval = () => {
    return isVisible.value === 'visible' ? CHECK_INTERVAL_MS : CHECK_INTERVAL_BACKGROUND_MS;
  };

  const interval = ref(getCheckInterval());
  const { pause, resume } = useIntervalFn(checkTokenExpiration, interval, { immediate: false });

  watch(isVisible, () => {
    interval.value = getCheckInterval();
  });

  function setupActivityListeners(): void {
    userActivityEvents.forEach((event) => {
      globalThis.addEventListener(event, handleUserActivity, { passive: true });
    });
  }

  function cleanupActivityListeners(): void {
    userActivityEvents.forEach((event) => {
      globalThis.removeEventListener(event, handleUserActivity);
    });
  }

  function initializeTokenRefresh(): void {
    const token = session();
    const isPublic = isPublicRoute();

    if (!token || isSessionClosed.value || isPublic) {
      return;
    }

    const sharedTimestampStr = localStorage.getItem(LAST_ACTIVITY_TIMESTAMP_KEY);
    if (sharedTimestampStr) {
      const sharedTimestamp = Number.parseInt(sharedTimestampStr, 10);
      if (Number.isNaN(sharedTimestamp)) {
        lastActivityTime = Date.now();
      } else {
        lastActivityTime = sharedTimestamp;
      }
    } else {
      lastActivityTime = Date.now();
    }

    setupActivityListeners();
    resume();
    checkTokenExpiration();
  }

  function stopTokenRefresh(): void {
    pause();
    cleanupActivityListeners();
    showExpirationAlert.value = false;
    alertShownAt = null;
  }

  onMounted(() => {
    setOnLogoutCallback(() => {
      isSessionClosed.value = true;
      pause();
      showExpirationAlert.value = false;
      alertShownAt = null;
      cleanupActivityListeners();
    });

    setOnUserActivityCallback((timestamp: number) => {
      if (isSessionClosed.value || !session()) {
        return;
      }
      if (timestamp > lastActivityTime) {
        lastActivityTime = timestamp;
      }
    });

    setOnExpirationAlertShownCallback((timeRemainingValue: number) => {
      const hasSession = Boolean(session());
      const isPublic = isPublicRoute();

      if (isSessionClosed.value || !hasSession) {
        return;
      }

      if (isPublic) {
        return;
      }

      if (!showExpirationAlert.value) {
        showExpirationAlert.value = true;
        timeRemaining.value = timeRemainingValue;
      }
    });

    setOnExpirationAlertHiddenCallback(() => {
      if (isSessionClosed.value || !session()) {
        return;
      }
      if (showExpirationAlert.value) {
        showExpirationAlert.value = false;
        alertShownAt = null;
      }
    });

    initializeTokenRefresh();

    watch(() => session(), (token, oldToken) => {
      if (token && !oldToken) {
        isSessionClosed.value = false;
        
        if (!isPublicRoute()) {
          initializeTokenRefresh();
        }
      } else if (!token && oldToken) {
        stopTokenRefresh();
      }
    });

    watch(() => route.name, (newRouteName, oldRouteName) => {
      const token = session();
      const wasPublic = isPublicRouteByName(oldRouteName);
      const isPublic = isPublicRoute();

      if (token && !isSessionClosed.value) {
        if (wasPublic && !isPublic) {
          initializeTokenRefresh();
        } else if (!wasPublic && isPublic) {
          stopTokenRefresh();
        }
      }
    });
  });

  onBeforeUnmount(() => {
    cleanupActivityListeners();
    pause();
  });

  return {
    showExpirationAlert,
    timeRemaining,
    extendSession,
    closeSession,
  };
}
